import { useTranslation } from 'react-i18next';
import { Label } from '@ooh/ui/components/label';

export interface CampaignNumberPreviewState {
  data?: { campaignNumber: string } | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Live preview of the campaign number the draft would receive if created
 * right now, including the ordinal (offer-draft-numbering /
 * "Campaign-number preview on the offer creation form"). `preview` comes
 * from `useCampaignNumberPreview`, which stays disabled — showing the
 * "incomplete" state below — until a client and a salesperson are both
 * selected. Never blocks or gates submission: this field only ever renders
 * text, with no effect on the creation form's submit button.
 *
 * Extracted from create-offer-page.tsx (mirrors BrandField's own
 * extraction) purely to keep that file's line count under the lint
 * threshold. Used only by the main creation form — the quick-add offer
 * picker's inline create form is a deliberate exclusion.
 */
export function CampaignNumberPreviewField({ preview }: { preview: CampaignNumberPreviewState }) {
  const { t } = useTranslation('offers');
  const content = preview.data
    ? preview.data.campaignNumber
    : preview.isLoading
      ? t('create.fields.campaignNumberPreviewLoading')
      : preview.isError
        ? t('create.fields.campaignNumberPreviewUnavailable')
        : t('create.fields.campaignNumberPreviewIncomplete');

  return (
    <div className="space-y-1.5" data-testid="field-campaign-number-preview">
      {/*
        `htmlFor` has no effect here: a `<p>` isn't a labelable form control
        (only input/textarea/select/button/meter/output/progress are), so
        pointing `htmlFor` at it would silently do nothing for screen
        readers. `aria-labelledby` on the `<p>` below is the correct
        association for a non-control element (PR #1141 review finding 4).
      */}
      <Label id="create-offer-campaign-number-preview-label">
        {t('create.fields.campaignNumberPreview')}
      </Label>
      <p
        id="create-offer-campaign-number-preview"
        data-testid="create-offer-campaign-number-preview"
        aria-labelledby="create-offer-campaign-number-preview-label"
        className={
          preview.data ? 'text-sm font-medium text-foreground' : 'text-sm text-muted-foreground'
        }
      >
        {content}
      </p>
    </div>
  );
}
